import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Landmark, RefreshCcw, Zap, ExternalLink, Calendar, Sparkles, Filter } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FILTER_STORAGE_KEY = "uid_schemes_hide_low_priority";

// Keywords used to identify low-priority MyGov content (contests, campaigns, auctions, feedback,
// design competitions etc.) — hidden when the "Real schemes only" toggle is on.
const LOW_PRIORITY_KEYWORDS = /(auction|contest|competition|design|logo|feedback|inputs|invitation|invite|inviting|tableaux|greetings|quiz|survey|poll|share your|share ideas|banner)/i;

function isLowPriority(scheme) {
  const hay = `${scheme.title || ""} ${scheme.summary || ""}`;
  return LOW_PRIORITY_KEYWORDS.test(hay);
}

const CATEGORY_TONE = {
  central_scheme: "bg-blue-500/15 text-blue-200 border-blue-400/30",
  state_scheme:   "bg-emerald-500/15 text-emerald-200 border-emerald-400/30",
  mission:        "bg-purple-500/15 text-purple-200 border-purple-400/30",
  initiative:     "bg-cyan-500/15 text-cyan-200 border-cyan-400/30",
  other:          "bg-white/10 text-white/70 border-white/20",
};

const SOURCE_TONE = {
  PIB:     "bg-orange-500/15 text-orange-200 border-orange-400/30",
  MyGov:   "bg-indigo-500/15 text-indigo-200 border-indigo-400/30",
  PMIndia: "bg-fuchsia-500/15 text-fuchsia-200 border-fuchsia-400/30",
};

function daysUntil(iso) {
  if (!iso) return null;
  try {
    return Math.ceil((new Date(iso) - new Date()) / (24 * 3600 * 1000));
  } catch { return null; }
}

function daysSince(iso) {
  if (!iso) return null;
  try {
    return Math.floor((new Date() - new Date(iso)) / (24 * 3600 * 1000));
  } catch { return null; }
}

function formatRelative(iso) {
  if (!iso) return "";
  const diffM = Math.max(0, Math.floor((new Date() - new Date(iso)) / 60000));
  if (diffM < 1) return "just now";
  if (diffM < 60) return `${diffM}m ago`;
  const h = Math.floor(diffM / 60);
  if (h < 24) return `${h}h ago`;
  return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
}

export default function LatestSchemesColumn() {
  const { t } = useTranslation();
  const [schemes, setSchemes] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hideLowPriority, setHideLowPriority] = useState(() => {
    try { return localStorage.getItem(FILTER_STORAGE_KEY) === "1"; } catch { return false; }
  });

  const toggleFilter = () => {
    setHideLowPriority((prev) => {
      const next = !prev;
      try { localStorage.setItem(FILTER_STORAGE_KEY, next ? "1" : "0"); } catch { /* private mode */ }
      return next;
    });
  };

  useEffect(() => {
    let alive = true;
    fetch(`${API}/schemes/latest?limit=20`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => {
        if (!alive) return;
        setSchemes(d.items || []);
        setLastUpdated(d.last_updated || null);
        setLoading(false);
      })
      .catch((e) => { if (alive) { setError(e.message); setLoading(false); } });
    return () => { alive = false; };
  }, []);

  return (
    <aside
      className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm p-4 sm:p-5 flex flex-col h-full min-h-[520px]"
      data-testid="latest-schemes-column"
    >
      {/* header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="grid h-8 w-8 place-items-center rounded-lg bg-blue-500/15 border border-blue-400/30 text-blue-300 shrink-0">
            <Landmark className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <h2 className="text-base font-bold leading-tight truncate" data-testid="latest-schemes-title">
              {t("home_dashboard.schemes.title")}
            </h2>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/50">
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 border border-emerald-400/30 px-1.5 py-0.5 text-emerald-300 font-bold">
                <Zap className="h-2.5 w-2.5" /> LIVE
              </span>
              <RefreshCcw className="h-3 w-3 opacity-60" />
              <span className="text-[10px] normal-case" data-testid="schemes-last-updated">
                {lastUpdated ? formatRelative(lastUpdated) : t("home_dashboard.schemes.updating")}
              </span>
            </div>
          </div>
        </div>
        {/* filter toggle — hides low-priority MyGov content (contests, campaigns) */}
        <button
          type="button"
          onClick={toggleFilter}
          title={hideLowPriority ? "Show all items" : "Hide contests / auctions / feedback"}
          className={`shrink-0 inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[10px] font-bold uppercase tracking-wider transition-colors ${
            hideLowPriority
              ? "border-emerald-400/50 bg-emerald-500/15 text-emerald-200 hover:bg-emerald-500/25"
              : "border-white/15 bg-white/[0.03] text-white/50 hover:text-white/80 hover:border-white/25"
          }`}
          data-testid="schemes-real-only-toggle"
        >
          <Filter className="h-2.5 w-2.5" />
          {hideLowPriority ? "REAL ONLY" : "SHOW ALL"}
        </button>
      </div>

      {/* list */}
      <div className="flex-1 overflow-y-auto pr-1 space-y-2 max-h-[460px] custom-scrollbar" data-testid="schemes-list">
        {loading && (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-14 rounded-lg bg-white/[0.03] border border-white/5 animate-pulse" />
          ))
        )}
        {error && !loading && (
          <div className="text-xs text-rose-200 p-3 rounded-lg border border-rose-500/30 bg-rose-500/10" data-testid="schemes-error">
            {t("home_dashboard.schemes.load_error")}
          </div>
        )}
        {!loading && !error && schemes.length === 0 && (
          <div className="text-xs text-white/50 p-3">{t("home_dashboard.schemes.empty")}</div>
        )}
        {!loading && !error && schemes.map((s) => {
          const cat = s.category || "other";
          const tone = CATEGORY_TONE[cat] || CATEGORY_TONE.other;
          const srcTone = SOURCE_TONE[s.source_name] || "bg-white/10 text-white/70 border-white/20";
          // Marathi-first display (user preference: option b). Falls back to original.
          const displayTitle = s.title_mr || s.title;
          const displaySummary = s.summary_mr || s.summary;
          // Deadline chip logic: red URGENT if last_date within 7d, else "NEW" if published within 7d
          const dLeft = daysUntil(s.last_date);
          const isUrgent = dLeft !== null && dLeft >= 0 && dLeft <= 7;
          const dSince = daysSince(s.published_at);
          const isNew = !isUrgent && dSince !== null && dSince >= 0 && dSince <= 7;
          return (
            <a
              key={s.external_id}
              href={s.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className={`group block rounded-lg border ${s.is_featured ? "border-amber-400/40 bg-amber-500/[0.04]" : "border-white/8 bg-white/[0.02]"} hover:bg-white/[0.06] hover:border-blue-400/40 transition-colors p-2.5`}
              data-testid={`schemes-item-${s.external_id?.slice(0, 8)}`}
            >
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                    {s.is_featured && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-amber-400/40 bg-amber-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-200" data-testid="scheme-featured-chip">
                        <Sparkles className="h-2.5 w-2.5" /> PIN
                      </span>
                    )}
                    <span className={`inline-flex items-center rounded-full border ${srcTone} px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide`}>
                      {s.source_name}
                    </span>
                    <span className={`inline-flex items-center rounded-full border ${tone} px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide`}>
                      {t(`home_dashboard.schemes.categories.${cat}`, { defaultValue: t("home_dashboard.schemes.categories.other") })}
                    </span>
                    {isUrgent && (
                      <span
                        className="inline-flex items-center gap-1 rounded-full border border-rose-400/50 bg-rose-500/20 px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wide text-rose-200 animate-pulse"
                        data-testid="scheme-urgent-chip"
                      >
                        <Calendar className="h-2.5 w-2.5" />
                        {dLeft === 0 ? t("home_dashboard.schemes.today") : `${dLeft}${t("home_dashboard.schemes.days_left_suffix")}`}
                      </span>
                    )}
                    {isNew && (
                      <span
                        className="inline-flex items-center rounded-full border border-emerald-400/40 bg-emerald-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-emerald-200"
                        data-testid="scheme-new-chip"
                      >
                        {t("home_dashboard.schemes.new_this_week")}
                      </span>
                    )}
                  </div>
                  <div className="text-[12.5px] font-semibold text-white/90 group-hover:text-white leading-snug line-clamp-2">
                    {displayTitle}
                  </div>
                  {displaySummary && (
                    <div className="mt-0.5 text-[10px] text-white/45 line-clamp-1">
                      {displaySummary}
                    </div>
                  )}
                </div>
                <ExternalLink className="h-3.5 w-3.5 text-white/30 group-hover:text-blue-300 group-hover:translate-x-0.5 transition-all shrink-0 mt-0.5" />
              </div>
            </a>
          );
        })}
      </div>
    </aside>
  );
}
